import { parseUrl } from 'query-string'

import { IS_JEST } from 'shared/consts'
import { CSRF_FIELD_NAME } from 'shared/consts/httpHeaders'
import logger from 'shared/services/logger'
import store from 'shared/store'
import { updateNowDataSuccessAction } from 'shared/store/ducks/now/actions'
import { updatePageProperties } from 'shared/store/ducks/publicRuntimeConfig'
import { setUserSession } from 'shared/store/ducks/userSession'
import buildClientAPI from 'views/providers/clientAPI'
import { isUserLoginStatusAPIActive } from 'views/utils/featureToggleUtils'

const getDataFromLocalStorage = dataIdentifier => {
  const localStorageData = localStorage.getItem(dataIdentifier)
  const parsedData = localStorageData ? !!JSON.parse(localStorageData) : false
  return parsedData
}

async function getUserLoginStatusFromAuthTokenWhenEnabled(
  clientFockService,
  userSession,
  isNxEnabled,
  useUserLoginStatus
) {
  if (!isNxEnabled && !useUserLoginStatus) {
    return userSession
  }

  let isLoggedIn = false
  await clientFockService
    .getUserLoginStatus()
    .then(response => {
      isLoggedIn = response.data.isLoggedInUserRegistered
    })
    .catch(() => {
      logger.error('Unable to fetch user login status')
      isLoggedIn = false
    })
    .finally(() => {
      userSession = {
        ...userSession,
        isLoggedIn,
      }
    })
  return userSession
}

const fetchUserSession = queryStr => {
  const { clientFockService, addressService } = buildClientAPI()

  const updateNowDataWithZipFromBillingAddress = async () => {
    const nowData = getDataFromLocalStorage('nowData')
    const isZipCodeSetOnceFromBillingAddress = getDataFromLocalStorage(
      'isZipCodeSetOnceFromBillingAddress'
    )

    // we return early if the user has already taken the ZIP from Billing Address,
    // or if nowData.zipCode has a value already
    if (isZipCodeSetOnceFromBillingAddress || nowData?.zipCode) {
      return
    }

    const billingAddress = await addressService.getBillingAddress()

    if (billingAddress?.zip) {
      store.dispatch(
        updateNowDataSuccessAction({
          zipCode: billingAddress.zip,
        })
      )
      localStorage.setItem('isZipCodeSetOnceFromBillingAddress', 'true')
    }
  }

  let params = {}

  const { query } = parseUrl(queryStr || window.location.search)

  if (query.expa && query.expa !== '') {
    params.expa = query.expa
  }

  const {
    pageProperties: { featureToggles, pzn, nx },
  } = store.getPublicRuntimeConfig()
  const useUserLoginStatus = isUserLoginStatusAPIActive(featureToggles)

  if (pzn) {
    params.pzn = pzn
  }

  if (window.STORYBOOK_ENV) {
    params = null
  }

  let userSession

  // TODO: use the fire/listen Event (used for crosssell)
  clientFockService
    .fetchUserSessionData({ params })
    .then(response => {
      const { data, headers: { [CSRF_FIELD_NAME]: csrfToken } = {} } = response
      // new way of doing it
      // needs to be added to components via connect
      // dispatch `updatePageProperties` first
      // b/c leads to re-render component with the wrong state

      store.dispatch(updatePageProperties({ csrfToken }))
      userSession = {
        ...data,
        subscriptionService:
          store.getState().publicRuntimeConfig.pageProperties.featureToggles
            .subscriptionServiceActive,
      }
    })
    .catch(error => {
      logger.error(`UserSessionData not fetched: ${error}`)
      userSession = {}
    })
    .finally(async () => {
      //TODO: Here userLogin api is only called in case if nx routing is enabled or when toggle useUserLoginStatus is true. This check of NX enabled was introduced to handle issues when LMS is the prime front end(Nx will be disabled). Remove this check when all pages are migrated to Fock
      userSession = await getUserLoginStatusFromAuthTokenWhenEnabled(
        clientFockService,
        userSession,
        nx,
        useUserLoginStatus
      )
      store.dispatch(setUserSession(userSession))

      // if the user is logged in and has no ZIP in localStorage,
      // we try and update nowData with ZIP code from the user's billing address
      if (
        userSession?.isLoggedIn &&
        // Skip request to external source in test, better to mock it
        !IS_JEST
      ) {
        await updateNowDataWithZipFromBillingAddress()
      }

      if (
        params?.expa &&
        // fetch is not defined in test environment
        !IS_JEST
      ) {
        // params could be null during testing
        fetch(`/expa?expa=${params.expa}`, {
          method: 'POST',
        })
      }
    })
}

export default fetchUserSession
